import React from "react";
import moment from "moment";
import { XIcon, InformationCircleIcon } from "@heroicons/react/outline";
import { RiGitRepositoryPrivateLine } from "react-icons/ri";
import { MdOutlinePublic } from "react-icons/md";

const ItemMetadata = ({ item, type, onClose, showFileVisibility = true }) => {
  if (!item) return null;

  const label = type === "folder" ? item.name : (item.description || item.name || "Untitled");
  const createdDate = item.created_at;

  const getIcon = () => {
    if (type === "folder") {
      return (
        <svg className='h-5 w-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' />
        </svg>
      );
    }
    return (
      <svg className='h-5 w-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
      </svg>
    );
  };

  return (
    <div className='bg-white border border-gray-200 rounded-lg shadow-sm'>
      {/* Header */}
      <div className='bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 px-4 py-3 border-b border-gray-200 rounded-t-lg'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2.5'>
            <div className={`p-2 rounded-lg shadow-sm ${type === "folder" ? 'bg-blue-100 border border-blue-200' : 'bg-gray-100 border border-gray-200'}`}>
              {getIcon()}
            </div>
            <div>
              <h4 className='text-sm font-bold text-gray-900'>
                {type === "folder" ? "Folder Information" : "File Information"}
              </h4>
              <p className='text-[10px] text-gray-500 mt-0.5'>
                {type === "folder" ? "View folder details" : "View file details"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className='p-1.5 rounded-lg hover:bg-white/90 transition-colors text-gray-400 hover:text-gray-700 active:scale-95'
          >
            <XIcon className='h-4 w-4' />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className='p-3'>
        <div className='space-y-2.5'>
          {/* Name */}
          <div className='pb-2 border-b border-gray-100'>
            <div className='flex items-start gap-2'>
              <span className='text-[10px] font-medium text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Name</span>
              <p className='text-xs text-gray-900 font-medium flex-1 break-words' title={label}>
                {label}
              </p>
            </div>
          </div>

          {/* File-specific: Size */}
          {type === "file" && item.size && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Size</span>
                <p className='text-xs text-gray-900 font-semibold'>
                  {item.size < 1024
                    ? item.size + " B"
                    : item.size < 1024 * 1024
                    ? (item.size / 1024).toFixed(2) + " KB"
                    : (item.size / (1024 * 1024)).toFixed(2) + " MB"}
                </p>
              </div>
            </div>
          )}

          {/* Created Date */}
          {createdDate && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>
                  {type === "folder" ? "Created" : "Uploaded"}
                </span>
                <p className='text-xs text-gray-900 font-semibold'>
                  {moment(createdDate).format("DD MMM YYYY, hh:mm A")}
                </p>
              </div>
            </div>
          )}

          {/* Modified Date */}
          {item.updated_at && item.updated_at !== createdDate && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Modified</span>
                <p className='text-xs text-gray-900 font-semibold'>
                  {moment(item.updated_at).format("DD MMM YYYY, hh:mm A")}
                </p>
              </div>
            </div>
          )}

          {/* File-specific: Expiry */}
          {type === "file" && item.expiry_time && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Expires</span>
                <div className='flex items-center gap-2 flex-wrap'>
                  <p className={`text-xs font-semibold ${item.is_expire &&item?.expiry_time && moment(item?.expiry_time).isBefore(moment(), 'day')? "text-red-600" : "text-gray-900"}`}>
                    {moment(item.expiry_time).format("DD MMM YYYY")}
                  </p>
                  {item.is_expire && item?.expiry_time && moment(item?.expiry_time).isBefore(moment(), 'day') && (
                    <span className='px-2 py-0.5 bg-red-50 text-red-600 rounded-full text-[10px] font-semibold border border-red-200'>
                      Expired
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* File-specific: Uploaded By */}
          {type === "file" && item.uploaded_by_details && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Uploaded By</span>
                <p className='text-xs text-gray-900 font-semibold'>{item.uploaded_by_details.name}</p>
              </div>
            </div>
          )}

          {/* File-specific: Visibility */}
          {showFileVisibility && type === "file" && item.visibility !== undefined && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Visibility</span>
                <div className='flex items-center'>
                  {item.visibility ? (
                    <div className='flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 text-gray-700 rounded-lg border border-gray-200'>
                      <RiGitRepositoryPrivateLine className='h-3.5 w-3.5' />
                      <span className='text-xs font-semibold'>Private</span>
                    </div>
                  ) : (
                    <div className='flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-200'>
                      <MdOutlinePublic className='h-3.5 w-3.5' />
                      <span className='text-xs font-semibold'>Public</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Folder-specific: Type */}
          {type === "folder" && (
            <div className='bg-gray-50 rounded-lg p-2.5 border border-gray-100'>
              <div className='flex items-start gap-2.5'>
                <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide min-w-[80px] flex-shrink-0'>Type</span>
                <div className='flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-200'>
                  {getIcon()}
                  <span className='text-xs font-semibold'>Folder</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemMetadata;

